import type { KeyboardEvent } from 'react';
import { cn } from './cn';

/**
 * Directory-list primitive presentation + keyboard helper (PRO-0320, ADR-015 /
 * DS-6 selection-list rule / NF-10). Successor to the selectable-row variants of
 * `selectableTableRow.ts` for master–detail *directory* selection: the host renders
 * a `role="listbox"` of `role="option"` rows (single-select), not a `<table>`.
 * Pure/deterministic — no network, DB, or state. `selectableTableRow.ts` is retained
 * for *mutation* tables (e.g. roster revoke).
 *
 * Deviation note (PRO-0320): the TIP names this module `directoryList.ts`. On the
 * Windows (case-insensitive) dev filesystem that collides with the `DirectoryList.tsx`
 * component (same base name, case-only difference), so the helper is named
 * `directoryListRow.ts` — parallel to its predecessor `selectableTableRow.ts`.
 */

/** Bordered listbox shell — `Card`-equivalent elevation. No `overflow-x` (AC7). */
export const directoryListShellClassName =
  'overflow-hidden rounded-lg border border-border bg-surface text-base shadow-sm';

/**
 * Token classes for a single `role="option"` row. `selected` drives the inset ring
 * + `bg-muted`; hover/active feedback and a visible `:focus-visible` ring are always
 * present (DS-7). `min-h-14` + padding holds the ≥ 44 × 44 px target (DS-11).
 */
export function directoryOptionClassName(selected: boolean): string {
  return cn(
    'flex min-h-14 cursor-pointer items-center gap-3 border-b border-border px-3 py-3 transition-colors ease-ds last:border-b-0',
    'hover:bg-muted active:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
    selected && 'bg-muted ring-2 ring-inset ring-primary',
  );
}

/** Decorative master→detail chevron — signals that selecting opens the detail pane. */
export function directoryOptionChevronClassName(selected: boolean): string {
  return cn(
    'h-5 w-5 shrink-0 transition-colors ease-ds',
    selected ? 'text-primary' : 'text-text-secondary',
  );
}

/**
 * Single-select listbox keyboard model (PRO-0320 D2 — roving tabindex, SF-001).
 * Activates on Enter/Space; moves the active option on ArrowDown/ArrowUp (**no wrap**
 * at the ends) and Home/End. Returns the next active index, or `null` when the key is
 * ignored (e.g. Tab) or activation occurred. Callers move DOM focus to the returned
 * index. No `aria-activedescendant` is used.
 */
export function directoryListKeydown(
  event: KeyboardEvent<HTMLElement>,
  ctx: { activeIndex: number; count: number; onActivate: () => void },
): number | null {
  const { activeIndex, count, onActivate } = ctx;

  switch (event.key) {
    case 'Enter':
    case ' ':
      event.preventDefault();
      onActivate();
      return null;
    case 'ArrowDown':
      event.preventDefault();
      return count === 0 ? null : Math.min(activeIndex + 1, count - 1);
    case 'ArrowUp':
      event.preventDefault();
      return count === 0 ? null : Math.max(activeIndex - 1, 0);
    case 'Home':
      event.preventDefault();
      return count === 0 ? null : 0;
    case 'End':
      event.preventDefault();
      return count === 0 ? null : count - 1;
    default:
      return null;
  }
}
